import { pantryColors, pantryTypography } from '@pantry/ui'
import React, { useMemo, useState } from 'react'
import type { UpdateUserPayload } from '../../../../gateways/breadstack/userApis'
import type { AssignedStore, UserDetail } from '../../../../types/user'
import type { WHLocation } from '../../../../types/warehouse'
import { USER_ROLE_IDS, USER_ROLE_OPTIONS_WITH_CUSTOM } from '../../constants'
import {
  PANTRY_STORE_PERMISSIONS,
  type PermissionId,
  ROLE_PERMISSION_PRESETS,
} from '../../permissions'
import {
  type SelectedLocations,
  buildLocationRoles,
} from '../../utils/inviteLocationRoles'
import { LocationGrid } from '../InviteUserDialog/LocationGrid'
import { PermissionGrid } from '../InviteUserDialog/PermissionGrid'
import {
  FieldError,
  Label,
  inputStyle,
  selectStyle,
} from '../InviteUserDialog/styleHelpers'

const p = pantryColors
const F = pantryTypography.fontFamily

// Default role used when the user has no assigned_stores yet (e.g. an invited
// user record). Mirrors the invite dialog's default so the permission map
// seeds with a coherent preset.
const DEFAULT_EDIT_ROLE_ID = USER_ROLE_IDS.GENERAL_MANAGER

interface EditFormState {
  firstName: string
  lastName: string
  isAdmin: boolean
  roleId: string
  permissions: Record<string, boolean>
  locations: SelectedLocations
}

interface EditFormErrors {
  firstName?: string
  lastName?: string
  roleId?: string
  permissions?: string
  locations?: string
}

function validate(values: EditFormState): EditFormErrors {
  const errors: EditFormErrors = {}
  if (!values.firstName.trim()) errors.firstName = 'First name is required'
  if (!values.lastName.trim()) errors.lastName = 'Last name is required'

  // Admin grants org-wide access — role / permissions / locations are not part
  // of the payload, so skip those validations entirely (mirrors invite).
  if (values.isAdmin) return errors

  if (!values.roleId) errors.roleId = 'Please select a role'
  if (values.roleId === USER_ROLE_IDS.CUSTOM_ROLE) {
    const anyChecked = Object.values(values.permissions).some(Boolean)
    if (!anyChecked) {
      errors.permissions = 'Custom role requires at least one permission'
    }
  }
  if (!values.locations.all && values.locations.warehouseIds.length === 0) {
    errors.locations = 'Select at least one location, or choose All locations'
  }
  return errors
}

function splitName(detail: UserDetail): {
  firstName: string
  lastName: string
} {
  if (detail.first_name || detail.last_name) {
    return { firstName: detail.first_name, lastName: detail.last_name }
  }
  const parts = (detail.full_name ?? '').trim().split(/\s+/)
  if (parts.length === 0) return { firstName: '', lastName: '' }
  if (parts.length === 1) return { firstName: parts[0], lastName: '' }
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') }
}

// Convert a list of permission keys to the boolean map the form keeps in state.
function presetToMap(keys: PermissionId[]): Record<string, boolean> {
  const map: Record<string, boolean> = {}
  for (const k of keys) map[k] = true
  return map
}

// Resolve the permission map for the primary assigned_store:
//   • custom_role → read `custom_roles` directly (the authoritative bespoke set)
//   • built-in role → seed from ROLE_PERMISSION_PRESETS[role_id]
//   • no store / unknown role → empty map (no permissions checked)
function permissionsFromStore(
  store: AssignedStore | undefined,
): Record<string, boolean> {
  if (!store) return {}
  if (store.role_id === USER_ROLE_IDS.CUSTOM_ROLE) {
    const out: Record<string, boolean> = {}
    const raw = store.custom_roles ?? {}
    for (const k of Object.keys(raw)) {
      out[k] = !!(raw as Record<string, unknown>)[k]
    }
    return out
  }
  return presetToMap(ROLE_PERMISSION_PRESETS[store.role_id] ?? [])
}

// Translate `location_roles` into the `SelectedLocations` shape the LocationGrid
// understands:
//   • empty / missing → All locations (backend "no restriction" sentinel)
//   • non-empty → collect the enabled warehouse_ids
function locationsFromStore(
  store: AssignedStore | undefined,
): SelectedLocations {
  if (!store) return { all: true, warehouseIds: [] }
  const lr = store.location_roles ?? []
  if (lr.length === 0) return { all: true, warehouseIds: [] }
  const warehouseIds = lr.filter((r) => r.enabled).map((r) => r.warehouse_id)
  return { all: false, warehouseIds }
}

// Build the `custom_roles` payload — every permission key the UI knows about
// → boolean. Mirrors the invite dialog so the backend gets a complete map.
function buildCustomRoles(
  permissions: Record<string, boolean>,
): Record<string, boolean> {
  const out: Record<string, boolean> = {}
  for (const k of PANTRY_STORE_PERMISSIONS) out[k] = !!permissions[k]
  return out
}

const readonlyInputStyle: React.CSSProperties = {
  ...inputStyle(false),
  background: p.gray[50],
  color: p.gray[400],
  cursor: 'default',
}

// ── Component ─────────────────────────────────────────────────────────────────

export interface EditFormProps {
  detail: UserDetail
  storeId: string
  warehouses: WHLocation[]
  isSaving: boolean
  // Server-side error from the update mutation, surfaced as a banner above the
  // footer buttons. Null/undefined hides it.
  submitError?: string | null
  onSave: (payload: UpdateUserPayload) => void
  onCancel: () => void
}

export const EditForm: React.FC<EditFormProps> = ({
  detail,
  storeId,
  warehouses,
  isSaving,
  submitError = null,
  onSave,
  onCancel,
}) => {
  const { firstName: initFirst, lastName: initLast } = splitName(detail)
  const primaryStore = detail.assigned_stores?.[0]

  const [form, setForm] = useState<EditFormState>(() => ({
    firstName: initFirst,
    lastName: initLast,
    isAdmin: detail.is_ga,
    roleId: primaryStore?.role_id ?? DEFAULT_EDIT_ROLE_ID,
    permissions: detail.is_ga
      ? presetToMap(ROLE_PERMISSION_PRESETS[DEFAULT_EDIT_ROLE_ID] ?? [])
      : permissionsFromStore(primaryStore),
    locations: locationsFromStore(primaryStore),
  }))

  const [errors, setErrors] = useState<EditFormErrors>({})
  const [submitted, setSubmitted] = useState(false)

  const setField = <K extends keyof EditFormState>(
    field: K,
    value: EditFormState[K],
  ) => {
    const next = { ...form, [field]: value } as EditFormState
    setForm(next)
    if (submitted) setErrors(validate(next))
  }

  const handleAdminToggle = (checked: boolean) => {
    setField('isAdmin', checked)
  }

  const handleRoleChange = (roleId: string) => {
    // Picking a built-in role re-seeds the permission map from its preset
    // (matches invite-dialog UX). Picking 'custom_role' preserves the current
    // permission selection so the user can refine it.
    const next: EditFormState = {
      ...form,
      roleId,
      permissions:
        roleId && roleId !== USER_ROLE_IDS.CUSTOM_ROLE
          ? presetToMap(ROLE_PERMISSION_PRESETS[roleId] ?? [])
          : form.permissions,
    }
    setForm(next)
    if (submitted) setErrors(validate(next))
  }

  const handlePermissionToggle = (key: string, checked: boolean) => {
    // Toggling any individual permission flips the role to 'custom_role' —
    // the selection has diverged from any preset.
    const next: EditFormState = {
      ...form,
      roleId: USER_ROLE_IDS.CUSTOM_ROLE,
      permissions: { ...form.permissions, [key]: checked },
    }
    setForm(next)
    if (submitted) setErrors(validate(next))
  }

  const handleLocationsChange = (locations: SelectedLocations) => {
    setField('locations', locations)
  }

  const handleSave = () => {
    setSubmitted(true)
    const errs = validate(form)
    setErrors(errs)
    if (Object.keys(errs).length > 0) return

    const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`.trim()

    if (form.isAdmin) {
      // Admin → grant org-wide access (`is_ga: true`) and send an empty
      // assigned_stores array. Mirrors the invite dialog's admin branch.
      onSave({
        full_name: fullName,
        is_ga: true,
        assigned_stores: [],
        channels: [],
      })
      return
    }

    const locationRoles = buildLocationRoles(form.locations, warehouses)

    // `permissions: []` matches the invite dialog — the backend derives the
    // active permission set from role_id (built-in) or custom_roles (custom).
    onSave({
      full_name: fullName,
      is_ga: false,
      assigned_stores: [
        {
          store_id: storeId,
          role_id: form.roleId,
          permissions: [],
          location_roles: locationRoles,
          custom_roles:
            form.roleId === USER_ROLE_IDS.CUSTOM_ROLE
              ? buildCustomRoles(form.permissions)
              : {},
        },
      ],
      channels: [],
    })
  }

  const group = (mb = 20): React.CSSProperties => ({ marginBottom: mb })

  // Memo the role-select options so React doesn't think the array changed
  // every render.
  const roleOptions = useMemo(() => USER_ROLE_OPTIONS_WITH_CUSTOM, [])

  return (
    <div>
      {/* Row: First Name + Last Name */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          ...group(),
        }}
      >
        <div>
          <Label required>First Name</Label>
          <input
            type="text"
            value={form.firstName}
            onChange={(e) => setField('firstName', e.target.value)}
            style={inputStyle(!!errors.firstName)}
          />
          <FieldError msg={errors.firstName} />
        </div>
        <div>
          <Label required>Last Name</Label>
          <input
            type="text"
            value={form.lastName}
            onChange={(e) => setField('lastName', e.target.value)}
            style={inputStyle(!!errors.lastName)}
          />
          <FieldError msg={errors.lastName} />
        </div>
      </div>

      {/* Email — read-only (cannot be changed via this API) */}
      <div style={group()}>
        <Label>Email</Label>
        <input
          type="email"
          value={detail.user_email}
          readOnly
          style={readonlyInputStyle}
          title="Email address cannot be changed"
        />
        <div
          style={{
            fontSize: 12,
            color: p.gray[400],
            marginTop: 4,
            fontFamily: F,
          }}
        >
          Email address cannot be changed after account creation.
        </div>
      </div>

      {/* TODO(phone): re-enable when the API supports a per-user phone field.
         <div style={group()}>
           <Label>Phone</Label>
           <input
             type="tel"
             value=""
             readOnly
             placeholder="—"
             style={readonlyInputStyle}
             title="Phone number is not yet supported by the API"
           />
         </div>
      */}

      {/* Admin toggle — replicates the invite dialog's is_ga checkbox so the
          editor can grant or revoke org-wide access. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 12px',
          background: p.gray[50],
          borderRadius: 6,
          marginBottom: 16,
        }}
      >
        <input
          id="edit-admin-toggle"
          type="checkbox"
          checked={form.isAdmin}
          onChange={(e) => handleAdminToggle(e.target.checked)}
          style={{
            width: 16,
            height: 16,
            accentColor: '#2563EB',
            cursor: 'pointer',
            margin: 0,
          }}
        />
        <label
          htmlFor="edit-admin-toggle"
          style={{
            fontSize: 13.5,
            fontWeight: 600,
            color: p.gray[700],
            fontFamily: F,
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          Admin
        </label>
        <span
          style={{
            fontSize: 12,
            color: p.gray[400],
            fontFamily: F,
            marginLeft: 4,
          }}
        >
          Grants full org-wide access. Skips the role / permission / location
          settings below.
        </span>
      </div>

      {/* Role / permission / location section — hidden when Admin is checked */}
      {!form.isAdmin && (
        <>
          <div style={{ marginBottom: 16 }}>
            <Label required>Role</Label>
            <select
              value={form.roleId}
              onChange={(e) => handleRoleChange(e.target.value)}
              style={selectStyle(!!errors.roleId)}
            >
              <option value="" disabled>
                Select a role
              </option>
              {roleOptions.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <FieldError msg={errors.roleId} />
          </div>

          <div style={{ marginBottom: 16 }}>
            <PermissionGrid
              selected={form.permissions}
              errorMsg={errors.permissions}
              onToggleOne={handlePermissionToggle}
            />
          </div>

          <div>
            <LocationGrid
              warehouses={warehouses}
              value={form.locations}
              onChange={handleLocationsChange}
              errorMsg={errors.locations}
            />
          </div>
        </>
      )}

      {/* TODO(2FA): re-enable when the API supports configuring 2FA on a user.
         <div
           style={{
             display: 'flex',
             alignItems: 'center',
             justifyContent: 'space-between',
             padding: '12px 0',
             borderTop: `1px solid ${p.gray[100]}`,
           }}
         >
           <div>
             <div style={{ fontSize: 13.5, fontWeight: 500, color: p.gray[700], fontFamily: F }}>
               Two-Factor Authentication
             </div>
             <div style={{ fontSize: 12, color: p.gray[400], marginTop: 2, fontFamily: F }}>
               Require 2FA for this user's account
             </div>
           </div>
         </div>
      */}

      {submitError && (
        <div
          role="alert"
          style={{
            marginTop: 20,
            padding: '10px 12px',
            background: p.red[50],
            border: `1px solid ${p.red[200]}`,
            borderRadius: 6,
            color: p.red[700],
            fontSize: 13,
            fontFamily: F,
          }}
        >
          {submitError}
        </div>
      )}

      {/* Footer buttons — rendered inline (SlideDrawer footer slot used for view mode) */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          marginTop: 24,
        }}
      >
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '8px 14px',
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 500,
            cursor: isSaving ? 'not-allowed' : 'pointer',
            border: `1px solid ${p.gray[300]}`,
            background: 'transparent',
            color: p.gray[600],
            fontFamily: F,
            opacity: isSaving ? 0.6 : 1,
            transition: 'all .15s',
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 500,
            cursor: isSaving ? 'not-allowed' : 'pointer',
            border: 'none',
            background: '#2563EB',
            color: '#fff',
            fontFamily: F,
            opacity: isSaving ? 0.7 : 1,
            transition: 'all .15s',
          }}
          onMouseEnter={(e) => {
            if (!isSaving) e.currentTarget.style.background = '#1D4ED8'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#2563EB'
          }}
        >
          {isSaving ? (
            <>
              <svg
                style={{ animation: 'spin 1s linear infinite' }}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
                width="14"
                height="14"
              >
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
              </svg>
              <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
              Saving…
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>
    </div>
  )
}
